import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const HERO_IMG = "/images/kids-reading.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-24 hidden select-none font-serif text-[22rem] leading-none text-outline lg:block"
      >
        18
      </div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 pb-24 md:px-12 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-plum"
          >
            <Sparkles size={14} /> A children's library & learning hub · Est. 2018
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-serif text-[clamp(3rem,9vw,7.5rem)] leading-[0.95] tracking-tight text-ink"
          >
            <MaskedLine delay={0.25}>Room to</MaskedLine>
            <MaskedLine delay={0.4}>
              <em className="text-plum">wonder,</em>
            </MaskedLine>
            <MaskedLine delay={0.55}>space to grow.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-md text-base leading-relaxed text-slate2 md:text-lg"
          >
            A warm, unhurried sanctuary where children read, make, tinker and
            discover who they are — far beyond the four walls of a classroom.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#collection"
              data-testid="hero-cta-explore-collection"
              className="rounded-full bg-plum px-8 py-4 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105"
            >
              Explore Our Book Collection
            </a>
            <a
              href="#visit"
              data-testid="hero-cta-plan-visit"
              className="rounded-full border border-ink/25 px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-plum hover:text-plum"
            >
              Plan a Visit
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-16 flex items-center gap-10"
          >
            {[
              ["7,000+", "books on the shelves"],
              ["6", "hands-on studios"],
              ["2018", "rooted in the community"],
            ].map(([num, label]) => (
              <div key={label} data-testid={`hero-stat-${label.split(" ")[0]}`}>
                <p className="font-serif text-3xl text-ink md:text-4xl">{num}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate2">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[3/4] overflow-hidden rounded-3xl"
          >
            <motion.img
              src={HERO_IMG}
              alt="Children reading together on the library floor"
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.15 }}
              className="h-full w-full object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24, rotate: -4 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            data-testid="hero-quote-card"
            className="absolute -bottom-8 -left-4 max-w-xs rounded-2xl border border-ink/10 bg-cream p-6 shadow-[0_24px_60px_-30px_rgba(93,46,70,0.45)] md:-left-12"
          >
            <p className="font-serif text-lg italic leading-snug text-ink">
              "Every child deserves a room where curiosity is the only
              curriculum."
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-plum">
              — Our founding belief
            </p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="flex justify-center pb-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <ArrowDown size={20} className="text-plum" />
        </motion.div>
      </motion.div>
    </section>
  );
};
